
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Clipboard, Share2, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CampaignLink } from '@/types/referral';

interface ReferralLinkCardProps {
  referralUrl: string | null;
  isCopying: boolean;
  onCopyLink: () => void;
  onShare: () => void;
  createCampaignLink: (campaignName: string, referralUrl: string) => Promise<CampaignLink | null>;
}

export function ReferralLinkCard({
  referralUrl,
  isCopying,
  onCopyLink,
  onShare,
  createCampaignLink
}: ReferralLinkCardProps) {
  const { toast } = useToast();
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [trackedUrl, setTrackedUrl] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  const handleCreateTrackedLink = async () => {
    if (!campaignName.trim()) {
      toast({
        title: "Campaign name required",
        description: "Please enter a name for your campaign",
        variant: "destructive"
      });
      return;
    }

    if (!referralUrl) {
      toast({
        title: "Error",
        description: "No referral URL available",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);
    try {
      const campaignLink = await createCampaignLink(campaignName.trim(), referralUrl);
      if (campaignLink) {
        setTrackedUrl(campaignLink.referral_url);
      }
    } catch (error) {
      console.error('Error creating campaign link:', error);
    } finally {
      setIsCreating(false);
    }
  };
  
  const handleCopyTrackedLink = async () => {
    if (!trackedUrl) return;

    try {
      await navigator.clipboard.writeText(trackedUrl);
      toast({
        title: "Campaign link copied",
        description: "Your tracked referral link has been copied to clipboard."
      });
      handleCloseDialog();
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Could not copy to clipboard. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCloseDialog = () => {
    setCampaignDialogOpen(false);
    setCampaignName('');
    setTrackedUrl('');
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Your Referral Link</CardTitle>
          <CardDescription>
            Share this unique link with friends and receive rewards when they sign up
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={referralUrl || ''} readOnly className="font-mono text-sm" />
          <Button
            onClick={onCopyLink}
            className="whitespace-nowrap"
            disabled={isCopying || !referralUrl}
          >
            <Clipboard className="h-4 w-4 mr-2" />
            {isCopying ? 'Copying...' : 'Copy Link'}
          </Button>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={onShare} 
            variant="outline" 
            className="w-full sm:w-auto mt-2"
            disabled={!referralUrl}
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share Now
          </Button>
          
          <Dialog open={campaignDialogOpen} onOpenChange={(open) => {
            if (!open) handleCloseDialog();
            else setCampaignDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full sm:w-auto mt-2"
                disabled={!referralUrl}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign Link
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create Tracked Referral Link</DialogTitle>
                <DialogDescription>
                  Add a campaign parameter to track the source of your referrals.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="campaign" className="text-right">
                    Campaign
                  </Label>
                  <Input
                    id="campaign"
                    placeholder="instagram, twitter, email..."
                    className="col-span-3"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                  />
                </div>
                {trackedUrl && (
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="trackedLink" className="text-right">
                      Tracked Link
                    </Label>
                    <div className="col-span-3">
                      <Input
                        id="trackedLink"
                        value={trackedUrl}
                        readOnly
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                {!trackedUrl ? (
                  <Button
                    type="submit"
                    onClick={handleCreateTrackedLink}
                    disabled={isCreating}
                  >
                    {isCreating ? 'Creating...' : 'Generate Link'}
                  </Button>
                ) : (
                  <Button type="submit" onClick={handleCopyTrackedLink}>Copy Link</Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}
